'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface QAAnswerFormProps {
  onSubmit: (answer: string) => Promise<void>;
  isSubmitting?: boolean;
  className?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MAX_ANSWER_LENGTH = 1000;
const MIN_ANSWER_LENGTH = 10;

// ============================================================================
// COMPONENT
// ============================================================================

export function QAAnswerForm({
  onSubmit,
  isSubmitting = false,
  className,
}: QAAnswerFormProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [answer, setAnswer] = useState('');

  const charCount = answer.length;
  const isOverLimit = charCount > MAX_ANSWER_LENGTH;
  const isTooShort = charCount < MIN_ANSWER_LENGTH;
  const canSubmit = !isTooShort && !isOverLimit && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit(answer.trim());
    setAnswer('');
    setIsExpanded(false);
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    setAnswer('');
    setIsExpanded(false);
  };

  return (
    <div className={cn('w-full', className)}>
      <AnimatePresence mode="wait">
        {!isExpanded ? (
          <motion.div
            key="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center justify-between"
          >
            <span className="text-foreground-secondary">
              Ответьте на этот вопрос
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="gap-2"
            >
              <Send className="h-4 w-4" />
              Ответить
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            <Textarea
              placeholder="Напишите ваш ответ..."
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              disabled={isSubmitting}
              className={cn(
                'min-h-[100px] resize-none',
                isOverLimit && 'border-destructive focus-visible:ring-destructive'
              )}
              autoFocus
            />

            <div className="flex items-center justify-between">
              <span
                className={cn(
                  'text-sm tabular-nums text-foreground-tertiary',
                  isOverLimit && 'text-destructive'
                )}
              >
                {charCount > 0 && `${charCount}/${MAX_ANSWER_LENGTH}`}
              </span>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Отмена
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Отправка...
                    </>
                  ) : (
                    'Опубликовать ответ'
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
